import { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import { toast } from 'react-toastify';
import LinkCheckStatus from './LinkCheckStatus';
import { useMailEditorConfig } from './mailEditorConfig';

// Fullscreen preview dialog: renders the current (unsaved) editor document
// server-side via the configured renderPreview and shows it in a desktop or
// mobile frame, together with the subject line and the link check result.
const PreviewDialog = ({ open, onClose, emailDoc }) => {
  const { renderPreview } = useMailEditorConfig();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('desktop');

  useEffect(() => {
    if (!open || !emailDoc || !renderPreview) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setPreview(null);
      try {
        const result = await renderPreview(emailDoc);
        if (!cancelled && result) setPreview(result);
      } catch (error) {
        if (!cancelled) toast.error('Fehler beim Laden der Vorschau');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, emailDoc, renderPreview]);

  const isMobile = mode === 'mobile';

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 2,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant='h6' sx={{ flexGrow: 1 }}>
            Vorschau
          </Typography>
          <ToggleButtonGroup
            exclusive
            size='small'
            value={mode}
            onChange={(event, next) => {
              if (next) setMode(next);
            }}
          >
            <ToggleButton value='desktop' aria-label='Desktop'>
              <Tooltip title='Desktop'>
                <DesktopWindowsIcon fontSize='small' />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value='mobile' aria-label='Mobil'>
              <Tooltip title='Mobil'>
                <PhoneIphoneIcon fontSize='small' />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <IconButton onClick={onClose} aria-label='Vorschau schließen'>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ px: 3, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant='body2' sx={{ fontWeight: 600 }}>
            {`Betreff: ${preview?.subject || emailDoc?.subject || '—'}`}
          </Typography>
          <Box sx={{ mt: 1 }}>
            {loading ? (
              <Typography variant='body2' color='text.secondary'>
                Link-Prüfung läuft…
              </Typography>
            ) : (
              <LinkCheckStatus linkCheck={preview?.linkCheck} okLabel='Link-Prüfung bestanden' />
            )}
          </Box>
        </Box>
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            backgroundColor: 'grey.200',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            p: 3,
          }}
        >
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          )}
          {!loading && preview?.html && (
            <Paper
              elevation={3}
              sx={
                isMobile
                  ? {
                      width: 375 + 24,
                      p: '12px',
                      borderRadius: '28px',
                      backgroundColor: '#111',
                      flexShrink: 0,
                    }
                  : { width: '100%', maxWidth: 900, height: '100%' }
              }
            >
              <Box
                component='iframe'
                title='E-Mail Vorschau'
                sandbox=''
                srcDoc={preview.html}
                sx={
                  isMobile
                    ? {
                        width: 375,
                        height: 'calc(100vh - 220px)',
                        border: 0,
                        borderRadius: '18px',
                        backgroundColor: '#fff',
                        display: 'block',
                      }
                    : { width: '100%', height: '100%', border: 0, display: 'block', backgroundColor: '#fff' }
                }
              />
            </Paper>
          )}
          {!loading && !preview?.html && (
            <Typography variant='body2' color='text.secondary' sx={{ mt: 4 }}>
              Vorschau nicht verfügbar.
            </Typography>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default PreviewDialog;
